import { useEffect, useState } from 'react';
import { createTournament, joinTournamentByCode, joinTournamentById, subscribeToOpenLobbies } from '../services/firestore';
import { trackEvent } from '../services/analytics';
import { avatarForId } from '../utils/avatar';
import { ensureSignedIn } from '../services/auth';
import { useAuthUser } from '../hooks/useAuthUser';
import { usePlayerProfile } from '../hooks/usePlayerProfile';
import AppHeader from './AppHeader';
import Leaderboard from './Leaderboard';
import PastChampions from './PastChampions';
import rockImg from '../assets/rock.png';
import paperImg from '../assets/paper.png';
import scissorsImg from '../assets/scissors.png';
import './Home.css';

function Home({ setMe, connectionError }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get('code') || '');
  // Jumping in via an invite link (?code=...) should drop the player straight
  // into the join screen with the code prefilled, skipping the landing choice.
  const [screen, setScreen] = useState(() => (code ? 'join' : 'landing'));
  const [lobbies, setLobbies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const authUser = useAuthUser();
  const profile = usePlayerProfile(authUser?.uid);

  useEffect(() => {
    return subscribeToOpenLobbies(setLobbies, console.error);
  }, []);

  const requireName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter your name first.');
      return null;
    }
    return trimmed;
  };

  const runAction = async (action) => {
    setBusy(true);
    setError('');
    try {
      const me = await action();
      trackEvent('enter_username');
      setMe(me);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    const trimmed = requireName();
    if (!trimmed) return;
    runAction(async () => {
      const user = await ensureSignedIn();
      const me = await createTournament(trimmed, user.uid);
      trackEvent('create_tournament');
      return me;
    });
  };

  const handleJoinByCode = () => {
    const trimmed = requireName();
    if (!trimmed) return;
    if (!code.trim()) {
      setError('Enter a room code.');
      return;
    }
    runAction(async () => {
      const user = await ensureSignedIn();
      return joinTournamentByCode(code, trimmed, user.uid);
    });
  };

  const handleJoinLobbyRow = (lobby) => {
    if (busy || lobby.playerCount >= lobby.maxPlayers) return;
    const trimmed = requireName();
    if (!trimmed) return;
    runAction(async () => {
      const user = await ensureSignedIn();
      return joinTournamentById(lobby.id, trimmed, user.uid);
    });
  };

  const handleBack = () => {
    setError('');
    setScreen('landing');
  };

  const lobbyBadge = (
    <span className="badge-blue">
      <span className="live-dot" /> {lobbies.length} open {lobbies.length === 1 ? 'lobby' : 'lobbies'}
    </span>
  );

  const lobbiesList = (
    <>
      {lobbies.length === 0 && <p className="hint">No open lobbies right now — create one!</p>}
      {lobbies.map((lobby) => {
        const avatar = avatarForId(lobby.id);
        const full = lobby.playerCount >= lobby.maxPlayers;
        return (
          <div
            key={lobby.id}
            className={`lobby-row${full ? ' full' : ''}`}
            onClick={() => handleJoinLobbyRow(lobby)}
          >
            <div className="lobby-avatar" style={{ background: avatar.background }}>
              {avatar.emoji}
            </div>
            <div className="lobby-info">
              <div className="lobby-name">{lobby.name}</div>
              <div className="lobby-meta">Host: {lobby.hostName} · Best of 3</div>
            </div>
            <div className={`lobby-slots${full ? ' full' : ''}`}>
              {lobby.playerCount}/{lobby.maxPlayers}
            </div>
          </div>
        );
      })}
    </>
  );

  if (screen === 'landing') {
    return (
      <div className="page">
        <div className="panel">
          <AppHeader
            subtitle="Rock · Paper · Scissors Tournament"
            topRight={<span style={{ fontSize: 20 }}>🏆</span>}
            middleItems={['Real-time Multiplayer', 'Best of 3 · Single Elimination', 'Up to 8 Players']}
            bottomLeft={lobbyBadge}
            bottomRight={<span className="blue-label">⚡ Real-time · Elimination · Bots available</span>}
          />

          <div className="home-body">
            <div className="home-hero">
              <div className="hero-icons">
                <img src={rockImg} alt="rock" />
                <img src={paperImg} alt="paper" />
                <img src={scissorsImg} alt="scissors" />
              </div>
              <div className="hero-title">
                <span className="red">RPS</span> <span className="blue">Champ</span>
              </div>
              <div className="hero-sub">Real-time tournament · Who will survive?</div>
              <div className="stats-row">
                <div className="stat">
                  <div className="stat-num">8</div>
                  <div className="stat-label">Max Players</div>
                </div>
                <div className="stat">
                  <div className="stat-num">{lobbies.length}</div>
                  <div className="stat-label">Open Lobbies</div>
                </div>
                <div className="stat">
                  <div className="stat-num">BO3</div>
                  <div className="stat-label">Match Format</div>
                </div>
              </div>
            </div>

            <div className="home-right landing-actions">
              {profile && (
                <div className="card stats-card">
                  <div className="setting-row">
                    <div className="setting-label">🏆 Your Points</div>
                    <div className="setting-val">{profile.totalPoints}</div>
                  </div>
                  {/* Rank only means anything for registered players — anonymous
                      profiles never appear on the global leaderboard (see
                      docs/auth-leaderboard-plan.md), so a rank value here would
                      just be stale/unreachable rather than current. */}
                  {!authUser?.isAnonymous && !!profile.rank && (
                    <div className="setting-row">
                      <div className="setting-label">Global Rank</div>
                      <div className="setting-val">#{profile.rank}</div>
                    </div>
                  )}
                </div>
              )}
              <button className="btn-ghost" onClick={() => setScreen('leaderboard')}>
                🏆 View Global Leaderboard
              </button>
              <button className="btn-ghost" onClick={() => setScreen('pastChampions')}>
                🏛️ Past Champions
              </button>
              <button className="action-tile action-tile-blue" onClick={() => setScreen('join')}>
                <span className="action-icon">🥇</span>
                <span className="action-text">
                  <span className="action-title">Join a Tournament</span>
                  <span className="action-subtitle">Browse open lobbies and jump in</span>
                </span>
                <span className="action-arrow">›</span>
              </button>
              <button className="action-tile action-tile-red" onClick={() => setScreen('host')}>
                <span className="action-icon">⚡</span>
                <span className="action-text">
                  <span className="action-title">Host a Tournament</span>
                  <span className="action-subtitle">Create a lobby and invite players</span>
                </span>
                <span className="action-arrow">›</span>
              </button>
              <p className="hint landing-hint">you'll set your name on the next screen</p>

              <div className="card lobbies-card landing-lobbies-card">
                <h3>
                  <span>
                    <span className="live-dot" /> Live Lobbies
                  </span>
                  <span>{lobbies.length}</span>
                </h3>
                {lobbiesList}
              </div>
            </div>
          </div>

          {connectionError && <p className="error">{connectionError}</p>}
        </div>
      </div>
    );
  }

  if (screen === 'leaderboard') {
    return <Leaderboard onBack={handleBack} />;
  }

  if (screen === 'pastChampions') {
    return <PastChampions onBack={handleBack} />;
  }

  if (screen === 'host') {
    return (
      <div className="page">
        <div className="panel">
          <AppHeader
            subtitle="Host a Tournament"
            middleItems={['Real-time Multiplayer', 'Best of 3 · Single Elimination', 'Up to 8 Players']}
            bottomLeft="👑 Set your name to create a room"
          />

          <div className="home-body single">
            <div className="home-right">
              <button className="btn-ghost" onClick={handleBack} disabled={busy}>
                ← Back
              </button>

              <div className="card">
                <h3>Your Player Name</h3>
                <input
                  className="input-field"
                  type="text"
                  placeholder="Enter your name..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={24}
                  disabled={busy}
                  autoFocus
                />
              </div>

              <button className="btn-primary" onClick={handleCreate} disabled={busy}>
                ⚡ Create Tournament
              </button>
            </div>
          </div>

          {error && <p className="error">{error}</p>}
          {connectionError && <p className="error">{connectionError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="panel">
        <AppHeader
          subtitle="Join a Tournament"
          middleItems={['Real-time Multiplayer', 'Best of 3 · Single Elimination', 'Up to 8 Players']}
          bottomLeft="🚪 Set your name to join a room"
          bottomRight={lobbyBadge}
        />

        <div className="home-body single">
          <div className="home-right">
            <button className="btn-ghost" onClick={handleBack} disabled={busy}>
              ← Back
            </button>

            <div className="card">
              <h3>Your Player Name</h3>
              <input
                className="input-field"
                type="text"
                placeholder="Enter your name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={24}
                disabled={busy}
                autoFocus
              />
            </div>

            <div className="card">
              <h3>Join with Room Code</h3>
              <div className="code-input-row">
                <input
                  className="code-input"
                  type="text"
                  placeholder="RPS-···"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={busy}
                />
                <button className="btn-join-code" onClick={handleJoinByCode} disabled={busy}>
                  Join →
                </button>
              </div>
            </div>

            <div className="card lobbies-card">
              <h3>
                <span>
                  <span className="live-dot" /> Open Lobbies
                </span>
                <span>{lobbies.length}</span>
              </h3>
              {lobbiesList}
            </div>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {connectionError && <p className="error">{connectionError}</p>}
      </div>
    </div>
  );
}

export default Home;
